import { useEffect, useRef } from 'react';

import { useLocation } from 'react-router-dom';

import { buildGa4PageLocation, trackGa4PageView, type PageViewMeta } from '@/lib/analytics/ga4';

type ResolvedPageView = PageViewMeta & {
  trackingKey: string;
};

function normalizeProfile(search: string): string | null {
  const profile = new URLSearchParams(search).get('profile')?.trim().toLowerCase() ?? '';
  return profile || null;
}

function withProfileTrackingKey(pathname: string, search: string): string {
  const profile = normalizeProfile(search);
  return profile ? `${pathname}?profile=${profile}` : pathname;
}

function resolvePageView(pathname: string, search: string): ResolvedPageView {
  const profile = normalizeProfile(search);

  if (pathname === '/') {
    return {
      trackingKey: pathname,
      pageTitle: 'Home',
      pageGroup: 'marketing',
      pageType: 'home',
      pageTemplate: '/',
    };
  }

  if (pathname === '/methodology') {
    return {
      trackingKey: pathname,
      pageTitle: 'Methodology',
      pageGroup: 'marketing',
      pageType: 'methodology',
      pageTemplate: '/methodology',
    };
  }

  if (pathname === '/account' || pathname === '/pricing') {
    return {
      trackingKey: pathname,
      pageTitle: 'Pricing',
      pageGroup: 'marketing',
      pageType: 'pricing',
      pageTemplate: '/pricing',
    };
  }

  if (pathname === '/privacy-policy') {
    return {
      trackingKey: pathname,
      pageTitle: 'Privacy Policy',
      pageGroup: 'legal',
      pageType: 'privacy_policy',
      pageTemplate: '/privacy-policy',
    };
  }

  if (pathname === '/terms-of-service') {
    return {
      trackingKey: pathname,
      pageTitle: 'Terms Of Service',
      pageGroup: 'legal',
      pageType: 'terms_of_service',
      pageTemplate: '/terms-of-service',
    };
  }

  if (pathname === '/cookie-policy') {
    return {
      trackingKey: pathname,
      pageTitle: 'Cookie Policy',
      pageGroup: 'legal',
      pageType: 'cookie_policy',
      pageTemplate: '/cookie-policy',
    };
  }

  if (pathname === '/refund-policy') {
    return {
      trackingKey: pathname,
      pageTitle: 'Refund Policy',
      pageGroup: 'legal',
      pageType: 'refund_policy',
      pageTemplate: '/refund-policy',
    };
  }

  if (pathname === '/reports/login') {
    return {
      trackingKey: pathname,
      pageTitle: 'My Reports Login',
      pageGroup: 'account',
      pageType: 'reports_login',
      pageTemplate: '/reports/login',
    };
  }

  if (/^\/reports\/access\/[^/]+$/.test(pathname)) {
    return {
      trackingKey: pathname,
      pageTitle: 'Reports Access',
      pageGroup: 'account',
      pageType: 'reports_access',
      pageTemplate: '/reports/access/:token',
    };
  }

  if (pathname === '/reports' || pathname === '/reports/recent') {
    return {
      trackingKey: withProfileTrackingKey(pathname, search),
      pageTitle: 'My Reports',
      pageGroup: 'report',
      pageType: 'reports_library',
      pageTemplate: '/reports',
      profile,
    };
  }

  if (pathname === '/compare') {
    return {
      trackingKey: withProfileTrackingKey(pathname, search),
      pageTitle: 'Compare Reports',
      pageGroup: 'report',
      pageType: 'compare_reports',
      pageTemplate: '/compare',
      profile,
    };
  }

  if (/^\/report\/[^/]+\/conclusion$/.test(pathname)) {
    return {
      trackingKey: withProfileTrackingKey(pathname, search),
      pageTitle: 'Property Conclusion',
      pageGroup: 'report',
      pageType: 'report_conclusion',
      pageTemplate: '/report/:idOrHash/conclusion',
      profile,
    };
  }

  if (/^\/report\/[^/]+\/save-money$/.test(pathname)) {
    return {
      trackingKey: withProfileTrackingKey(pathname, search),
      pageTitle: 'Report Savings Guide',
      pageGroup: 'report',
      pageType: 'report_savings_guide',
      pageTemplate: '/report/:idOrHash/save-money',
      profile,
    };
  }

  if (/^\/report\/[^/]+$/.test(pathname)) {
    return {
      trackingKey: withProfileTrackingKey(pathname, search),
      pageTitle: 'Property Report',
      pageGroup: 'report',
      pageType: 'full_report',
      pageTemplate: '/report/:idOrHash',
      profile,
    };
  }

  if (/^\/shared\/[^/]+$/.test(pathname)) {
    return {
      trackingKey: pathname,
      pageTitle: 'Shared Report',
      pageGroup: 'report',
      pageType: 'shared_report',
      pageTemplate: '/shared/:token',
    };
  }

  return {
    trackingKey: pathname,
    pageTitle: pathname === '/404' ? 'Page Not Found' : 'Public Page',
    pageGroup: 'public',
    pageType: pathname === '/404' ? 'not_found' : 'generic_public',
    pageTemplate: pathname || '/',
  };
}

export function PublicAnalyticsTracker() {
  const location = useLocation();
  const previousPageLocationRef = useRef<string | null>(null);
  const previousTrackingKeyRef = useRef<string | null>(null);

  useEffect(() => {
    const nextView = resolvePageView(location.pathname, location.search);
    if (previousTrackingKeyRef.current === nextView.trackingKey) return;

    const { trackingKey, ...pageViewMeta } = nextView;

    trackGa4PageView({
      ...pageViewMeta,
      pageReferrer: previousPageLocationRef.current,
    });

    previousTrackingKeyRef.current = trackingKey;
    previousPageLocationRef.current = buildGa4PageLocation(nextView.pageTemplate, nextView.profile);
  }, [location.pathname, location.search]);

  return null;
}
